import { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const topRef = useRef<HTMLDivElement>(null);
  const comic = comics.find((c) => c.id === Number(id));

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center mb-4">
              <i className="ri-file-search-line text-4xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的漫画不存在或已下架</p>
            <Link to="/" className="px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 6);

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50" ref={topRef}>
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '漫画分类', href: '/category/all' },
              { label: comic.tags[0] || '漫画', href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-8 mb-10">
            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full md:w-64">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 下拉式漫画免费在线阅读`}
                    title={`${comic.title} - 漫画下拉式阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-1">
                  作者：{comic.author}
                </p>
                <p className="text-xs text-foreground-400 mb-4">
                  更新时间：{comic.updateTime}
                </p>

                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <span
                    className={`px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-primary-500 text-white'
                        : 'bg-secondary-500 text-white'
                    }`}
                  >
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-600">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-primary-500"></i>
                    </span>
                    {comic.rating} 分
                  </span>
                  <span className="flex items-center gap-1 text-sm text-foreground-600">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-sm"></i>
                    </span>
                    {comic.views} 次阅读
                  </span>
                  <span className="text-sm text-foreground-600">
                    共 {comic.chapters.length} 话
                  </span>
                </div>

                <div className="flex items-center gap-2 mb-5 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100 rounded-lg p-4 mb-5">
                  <h2 className="text-sm font-semibold text-foreground-900 mb-2 font-heading">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.description}
                  </p>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to={`/comic/${comic.id}`}
                    className="px-6 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </Link>
                  <button className="px-5 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-lg hover:bg-primary-50 hover:text-primary-600 border border-background-200/70 transition-colors whitespace-nowrap cursor-pointer">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </article>

          <section className="mb-14">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
              章节列表
            </h2>
            <p className="text-sm text-foreground-500 mb-5">
              共 {comic.chapters.length} 话 · 下拉式漫画阅读，流畅体验
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/comic/${comic.id}`}
                  className="px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 text-sm text-foreground-700 hover:text-primary-600 transition-all duration-200 truncate whitespace-nowrap cursor-pointer"
                >
                  {chapter.title}
                </Link>
              ))}
            </div>
          </section>

          {relatedComics.length > 0 && (
            <section className="mb-14">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {relatedComics.map((c) => (
                  <ComicCard
                    key={c.id}
                    id={c.id}
                    title={c.title}
                    author={c.author}
                    cover={c.cover}
                    tags={c.tags}
                    status={c.status}
                    rating={c.rating}
                    views={c.views}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 下拉式漫画免费在线阅读`,
            description: comic.description,
            author: { '@type': 'Person', name: comic.author },
            datePublished: comic.chapters[0]?.updateTime || comic.updateTime,
            dateModified: comic.updateTime,
            url: `https://43423mh.com/comic/${comic.id}`,
            publisher: {
              '@type': 'Organization',
              name: '43423漫画网',
              url: 'https://43423mh.com/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://43423mh.com/' },
                { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://43423mh.com/category/all' },
                { '@type': 'ListItem', position: 3, name: comic.tags[0] || '漫画', item: `https://43423mh.com/category/${comic.category}` },
                { '@type': 'ListItem', position: 4, name: comic.title },
              ],
            },
          }),
        }}
      />
    </>
  );
}